// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LDProvider, useLDClient, useLDClientError } from 'launchdarkly-react-client-sdk';
import type { LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import { useAuth } from '../hooks/useAuth';
import { personaForRole } from '../hooks/usePersona';
import { useActiveSite, FLEET_ID } from '../hooks/useActiveSite';
import { useShellStore } from '../stores/shellStore';
import { LAUNCHDARKLY_CLIENT_SIDE_ID } from '../config/featureFlags';

const ANONYMOUS_CONTEXT: LDContext = { kind: 'user', key: 'helios-ui-anonymous', anonymous: true };

/**
 * Placeholder for "no organization resolved", so a missing org targets as
 * its own thing rather than colliding with a real one.
 *
 * Safe only because of the type, not the spelling: `organization.key` is a
 * bare org id and org ids are `UUID` (`MeResponse.organization_id`), so
 * `'unknown-org'` cannot collide with one. Site ids are *not* UUIDs —
 * `SiteSummary.site_id` and `OmsSite.siteId` are both `str` — so `site.key`
 * cannot rely on spelling and carries an explicit scope segment instead
 * (`buildSiteContextKey`). Allowing a free-form org id would reopen the
 * collision here.
 */
const UNKNOWN_ORG = 'unknown-org';

const MAX_IDENTIFY_RETRIES = 2;
const IDENTIFY_RETRY_DELAY_MS = 1000;

const IDENTIFY_ATTEMPT_TIMEOUT_MS = 5000;

/**
 * Races `client.identify()` against a timer so a request that never settles
 * still produces a rejection after `timeoutMs`. Cannot cancel the underlying
 * request — the SDK's `identify()` gives back no abort handle — so it can
 * still complete after the timeout fires.
 *
 * A late *rejection* is safe to drop: nothing about the SDK's state changed,
 * and this attempt was already charged to the retry budget. A late *success*
 * is not safe to drop the same way: `identify()` applies its result
 * unconditionally (`launchdarkly-js-sdk-common`'s `identify()`, unlike its
 * streaming `ping` handler, has no staleness guard of its own), so if a
 * *different* identity became current in the time between the timeout and
 * this late success, the SDK's live flags now silently reflect the
 * abandoned identity while nothing downstream was ever told. `onLateSuccess`
 * exists to hand that back to the caller, which already knows how to check
 * whether the identity is still current and correct for it if not — the
 * same out-of-order handling this file already does for two concurrent
 * identify() calls.
 */
function identifyWithTimeout(
  client: LDClient,
  context: LDContext,
  timeoutMs: number,
  onLateSuccess: () => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`identify() did not settle within ${timeoutMs}ms`));
    }, timeoutMs);
    client.identify(context).then(
      () => {
        if (settled) {
          onLateSuccess();
          return;
        }
        settled = true;
        clearTimeout(timer);
        resolve();
      },
      err => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * True once a flag read reflects the *resolved* identity's real value, not
 * an in-between state. Exported as a context (not derived from
 * `useLDClient()` alone) because "the client object exists" and "we've
 * identified for the identity this render actually has" are different
 * moments — see AB#29037.
 * No client-side ID configured means nothing to wait for; the catalog
 * default is already final.
 */
const FlagsReadyContext = createContext(false);
const FlagsUnreachableContext = createContext(false);

/**
 * True once LaunchDarkly was given up on for the current identity (every identify retry
 * failed), so a gate that waits for readiness can tell the user instead of staying blank
 * forever (AB#35012). Never true while ready.
 */
export function useFlagsUnreachableContext(): boolean {
  return useContext(FlagsUnreachableContext);
}

export function useFlagsReadyContext(): boolean {
  return useContext(FlagsReadyContext);
}

/**
 * The resolved `site` context kind, built by the provider below and passed
 * through as one value so `buildContext` stays a straight projection. See
 * the targeting model below for what each field means and why.
 */
interface SiteTarget {
  /** Org-qualified, globally unique — see the targeting model below. */
  key: string;
  /** Human-facing short code; `''` in fleet mode or before sites resolve. */
  siteKey: string;
  /** Scoped to the whole fleet rather than one site. */
  fleet: boolean;
}

/**
 * Targeting model (AB#29035, D3 — decided for stage 1: opaque ids and
 * role/persona/org as attributes, no PII sent to LaunchDarkly). A
 * multi-kind context, not one flat "user" context with an orgId attribute
 * bolted on — LaunchDarkly's contexts model treats "which org" as its own
 * targetable dimension:
 *   - user.key: the IAM user id (`/me`'s `id`) — stable, not the email.
 *   - user.role: the resolved role from `/me`.
 *   - user.persona: `personaForRole(role)` — the same mapping `usePersona()`
 *     uses internally, but not `usePersona()` itself: that hook also layers
 *     in a manual/dev persona override from `shellStore` (an admin viewing
 *     the app as "Operator"), which is a display choice, not this user's
 *     real role — targeting should follow the real role, so this
 *     deliberately doesn't read the override.
 *   - organization.key: the bare org id from `MeResponse.organization_id`,
 *     for an org-scoped rollout. It is a `UUID`, not a display slug — so a
 *     rule reads it as an opaque identifier rather than as text, and because
 *     a UUID can contain no `:`, it is also what makes the org prefix on
 *     `site.key` below unambiguous.
 *   - site.key: `<orgId>:site:<siteId>` — the site the user is **currently
 *     viewing** in the Helios shell — or `<orgId>:fleet` when a manager is
 *     scoped to the whole fleet, and `<orgId>:unknown` while no site has
 *     resolved yet. Lets a flag roll out by data center, e.g. GA-10 first
 *     and GA-11 later, instead of only by org or role.
 *
 *     **A UI selection, not an identity.** It follows the Helios shell's
 *     site switcher (`useActiveSite()` resolved over
 *     `shellStore.selectedSiteId`), so a user who may switch sites moves
 *     themselves between targeting buckets with a dropdown. That is a
 *     stage-1 limitation, recorded rather than papered over: there is no
 *     per-user site to target on yet. `useAuth().sites` is the *org-wide*
 *     OMS roster — identical for every user in the org, see AuthContext.tsx
 *     — and `/me`'s own `sites` are filtered by SITE-scoped IAM
 *     assignments IAM does not issue yet. That last part is a *data* gap,
 *     not a code gap: the BFF already computes `sites` from the user's
 *     assignments, IAM just issues none, so `MeResponse.sites` arrives
 *     typed and empty. When IAM does issue them, that assignment is what a
 *     true per-data-center rollout should target, and this attribute
 *     narrows to per-view rules. Until then, read a site rule as "who is
 *     looking at GA-10 right now", never as "who belongs to GA-10".
 *
 *     **One selection across both shells.** Admin Center uses the
 *     shared/auth/useSiteSelection adapter; it does not import Helios'
 *     store directly. Either switcher updates this context. Which sites
 *     the switchers offer is not decided here: that is the org-targeted
 *     `site-rollout` flag, applied by contexts/SiteRolloutRoster.tsx.
 *
 *     **Org-qualified, and keyed on the stable site id.** OMS guarantees
 *     `siteKey` uniqueness only *within* an organization, and every org
 *     shares the `__fleet__` sentinel — so a bare site key would let one
 *     org's targeting rule enable a flag for another org's tenant. The key
 *     is therefore prefixed with the org id, and uses OMS' stable
 *     `site_id` rather than the human-facing short code. The prefix is the
 *     *user's own* org, so a rule matching on `site.key` can never cross a
 *     tenant boundary even if a foreign site ever reached the roster. That
 *     guarantee belongs to `site.key` alone — it does not extend to
 *     `site.siteKey`, which is org-scoped (see below). Shape and reserved
 *     states: `buildSiteContextKey`.
 *   - site.siteKey: the human-facing short code, e.g. `'site_ga10_atlanta'`
 *     — readable, but **org-scoped, so it is not safe to match on alone**.
 *     OMS guarantees it unique only within an organization, so
 *     `siteKey is one of ["site_ga10_atlanta"]` fires for that code in
 *     *every* org that has one. A rule that must fire for exactly one real
 *     site therefore reads either `site.key is one of
 *     ["<orgId>:site:<siteId>"]`, or `siteKey is one of [...]` **together
 *     with** a matching `organization.key` condition.
 *     Two distinct situations produce `''`: a fleet-scoped view, and a
 *     site that has not resolved yet (a cold load, or an OMS roster that
 *     failed to load). They are distinguishable — `fleet` is true only for
 *     the first, and `site.key` is exactly `<org>:unknown` only for the
 *     second (match the whole value, not a `:unknown` suffix: a real site
 *     whose id is literally `unknown` keys as `<org>:site:unknown`) —
 *     but a rule has to say so. `''` is *present*, not absent, so
 *     `siteKey is not one of [GA-10]` matches both of those on top of the
 *     sites you meant; prefer the positive form. A site-scoped *kill*
 *     switch should target `site.key` directly, since the unresolved case
 *     would otherwise leave the flag on for the very load you are trying
 *     to protect.
 *   - site.fleet: true when the manager is scoped to the whole fleet, so a
 *     rule can say "fleet views only" without string-matching a per-org
 *     sentinel. Recomputed from the *real* role and the raw selection, not
 *     read off `useActiveSite()`'s own `isFleet`, which *does* run through
 *     `usePersona()`'s manual/dev override (see the `user.persona` note
 *     above): letting that override decide "fleet" vs "one specific site"
 *     would be exactly the display-choice-leaking-into-targeting the
 *     persona note exists to prevent.
 */
/**
 * `site.key`: `<org>:fleet`, `<org>:unknown`, or `<org>:site:<siteId>`.
 *
 * Org-qualified because OMS guarantees site uniqueness only *within* an
 * organization and every org shares the `__fleet__` sentinel, so a bare
 * site key would let one tenant's rule match another's. The org half is a
 * `UUID` (`MeResponse.organization_id`), so it can contain no `:` and the
 * prefix is unambiguous however the rest is spelled.
 *
 * The scope segment is what keeps the two reserved states out of the real
 * site namespace. Site ids are free-form `str` (`SiteSummary.site_id`,
 * `OmsSite.siteId`), so a real site may legitimately be called `__fleet__`
 * or `unknown-site`; tagging real sites with `site:` means such an id lands
 * on `<org>:site:__fleet__` and cannot alias `<org>:fleet`.
 *
 * Takes `isFleet` and the raw site id rather than one pre-collapsed scope
 * string, deliberately. Deciding "is this fleet?" by comparing the scope
 * against `FLEET_ID` would reintroduce the same aliasing one layer down: a
 * real site whose id *is* `__fleet__` is indistinguishable from fleet mode
 * once the two share a slot. The caller knows which it meant, so it says so.
 */
function buildSiteContextKey(
  orgId: string | null | undefined,
  isFleet: boolean,
  siteId: string | null | undefined
): string {
  const org = orgId ?? UNKNOWN_ORG;
  if (isFleet) return `${org}:fleet`;
  if (!siteId) return `${org}:unknown`;
  return `${org}:site:${siteId}`;
}

function buildContext(
  meResponse: ReturnType<typeof useAuth>['meResponse'],
  effectiveRole: ReturnType<typeof useAuth>['effectiveRole'],
  orgId: ReturnType<typeof useAuth>['orgId'],
  site: SiteTarget
): LDContext {
  if (!meResponse) return ANONYMOUS_CONTEXT;
  return {
    kind: 'multi',
    user: {
      key: meResponse.id,
      anonymous: false,
      role: effectiveRole ?? 'unknown',
      persona: personaForRole(effectiveRole),
    },
    organization: { key: orgId ?? UNKNOWN_ORG },
    site: { key: site.key, siteKey: site.siteKey, fleet: site.fleet },
  };
}

/**
 * Re-identifies LaunchDarkly whenever the resolved identity changes — login,
 * logout, a role change, or switching the active site — and reports when
 * that specific identify has settled, so `FlagsReadyContext` can mean
 * "ready for this identity", not
 * just "ready for whichever identity happened to be current at mount"
 * (which, on a cold load, is the anonymous placeholder — see the module
 * doc comment above).
 *
 * `LDProvider`'s own `context` prop looked sufficient but isn't: its
 * `componentDidUpdate` only calls `identify()` again on the very first
 * transition from no context to a context, and only when `deferInitialization`
 * is set. Neither condition ever held here (context was never literally
 * `undefined`, and `deferInitialization` wasn't set), so after the initial
 * mount — always with the anonymous context, since `/me` hasn't resolved yet
 * at that point — the real per-user context was never sent. Every browser was
 * evaluated under the single shared anonymous key, permanently. Confirmed
 * against the SDK's own source, not assumed (AB#29037).
 *
 * `onSettled` takes `string | null` deliberately, not just `string`: a
 * `null` withdraws readiness. Two situations need that, not just "a new
 * identify started":
 *
 * 1. Out-of-order identify() calls. If identify(A) is still in flight when
 *    the identity changes to B, both are in-flight at once and nothing
 *    guarantees B's resolves last. `identifyLatest` always chases whichever
 *    identity `latestRef` currently holds, but a resolution for a
 *    superseded identity means the SDK may have *already applied* that
 *    stale identity before this callback even ran — the settled identity
 *    this component last reported can no longer be trusted, so readiness is
 *    withdrawn (`onSettled(null)`) before re-issuing, not just silently
 *    corrected. "Ready, but reading the wrong identity's values" is worse
 *    than "not ready".
 * 2. The initial mount's own LDProvider init timing out. `timeout={5}` (see
 *    the module doc below) bounds `waitForInitialization()`, but doesn't
 *    cancel the underlying request — read straight from
 *    launchdarkly-react-client-sdk@3.9.4's own `provider.tsx`
 *    (`prepareLDClient`): on a timeout it registers `ldClient.on('ready',
 *    ...)` for whenever that original (mount-time, effectively anonymous)
 *    request eventually completes, and that handler unconditionally
 *    overwrites `useFlags()` with whatever it resolves to — independent of,
 *    and invisible to, any `identify()` this component has since issued.
 *    Only reachable when `useLDClientError()` is currently set (the timeout
 *    path); on a late `'ready'`, this force-clears readiness and re-issues
 *    for the current identity so it — not the stale mount-time one — wins
 *    the SDK's last word. This narrows the window rather than closes it
 *    (the SDK applies its own state before either listener runs); genuinely
 *    closing it would mean forking `launchdarkly-react-client-sdk`'s own
 *    init/timeout handling, not something to take on here.
 * 3. The client instance itself gets replaced with the identity unchanged.
 *    `useLDClient()` can return a genuinely new object — `LDProvider`
 *    constructs a fresh `LDClient` on every `componentDidMount`, and
 *    StrictMode dev-mounts every component twice — while `contextKey` stays
 *    the same (the identity didn't change, only the client instance did).
 *    Keying "already identified for this" on the identity string alone
 *    missed this: the new client, the one `useFlags()` now actually reads,
 *    never got identified at all, while readiness stayed granted from the
 *    old one. `requestedRef` and every staleness check below key on the
 *    `(client, key)` pair, not the key alone, so a client swap is treated
 *    exactly like an identity change — something that needs its own
 *    identify(), not something already satisfied.
 *
 * A related, narrower residual this file accepts rather than works around:
 * `LDProvider`'s own `subscribeToChanges(ldClient)` (in
 * `provider.tsx`) is never unsubscribed — the class provider has no
 * `componentWillUnmount` (unlike `asyncWithLDProvider`, which does clean
 * up) — so a *replaced* client's `'change'` event can still fire against
 * `LDProvider`'s closure and overwrite `useFlags()` even once
 * `state.ldClient` has moved on to a different instance. Unlike scenario 2
 * above, this file has no listener to hook and cannot react to it at
 * all — the `subscribeToChanges` closure is internal to `LDProvider`, not
 * visible here — only tolerate it. The same answer still applies: every
 * gate downstream is fail-closed regardless, so the exposure is "briefly
 * wrong values", not "briefly over-permissive" — genuinely closing it would
 * again mean forking `launchdarkly-react-client-sdk`, not something to take
 * on here.
 *
 * That "fail-closed regardless" claim is about *readiness*, not about flag
 * values only ever widening: an org or role change can already move a flag
 * from enabled to disabled, so identity transitions were never monotonic.
 * What `site.key` adds is that the transition is now driven by an in-app
 * control a user operates directly,
 * so it happens routinely rather than at login or a role grant. Every
 * consumer gated on `useFlagsReadyContext()` (Admin Center's own
 * `RequireAdminCenterFlag`) is still fine — it blanks during the gap. A
 * future site-targeted flag read via `useFeatureFlag()` with no readiness
 * gate of its own would not be: it would keep showing the previous site's
 * value until the new identify() resolves, not "off".
 */
function LaunchDarklyIdentitySync({
  context,
  contextKey,
  onSettled,
  onGaveUp,
}: {
  context: LDContext;
  contextKey: string;
  onSettled: (key: string | null) => void;
  onGaveUp: (key: string | null) => void;
}) {
  const client = useLDClient();
  const ldClientError = useLDClientError();
  const latestRef = useRef({ context, contextKey, client });
  // A commit-phase write, not a render-phase one: React does not guarantee a
  // render-phase ref mutation survives a discarded concurrent render, and
  // nothing here needs the value before the first identify() can possibly
  // resolve (async, always after commit).
  useEffect(() => {
    latestRef.current = { context, contextKey, client };
  });
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const requestedRef = useRef<{ client: unknown; key: string | null }>({
    client: undefined,
    key: null,
  });

  const retryStateRef = useRef<{ client: unknown; key: string | null; attempt: number }>({
    client: undefined,
    key: null,
    attempt: 0,
  });
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    },
    []
  );

  const generationRef = useRef(0);

  // Stable across renders (assigned every render, called from two separate
  // effects below) so both "a new identity is desired" and "the SDK's
  // identity may have just been clobbered out from under us" drive the same
  // single chase-the-latest logic instead of two divergent copies of it.
  const identifyLatestRef = useRef<() => void>(() => {});
  identifyLatestRef.current = () => {
    if (!client) return;
    const { context: targetContext, contextKey: targetKey } = latestRef.current;
    if (requestedRef.current.client === client && requestedRef.current.key === targetKey) {
      return;
    }
    onSettled(null);
    // A new attempt withdraws a give-up too, or the notice would outlive it: the identity
    // can come back to a key we gave up on (site A, B, A) while A is being tried again.
    onGaveUp(null);
    requestedRef.current = { client, key: targetKey };
    const targetClient = client;
    const myGeneration = ++generationRef.current;

    function handleIdentifySuccess() {
      if (!mountedRef.current) return;
      if (latestRef.current.client !== targetClient) {
        return;
      }
      if (latestRef.current.contextKey !== targetKey) {
        onSettled(null);
        requestedRef.current = { client: undefined, key: null };
        identifyLatestRef.current();
        return;
      }
      onSettled(targetKey);
      if (generationRef.current !== myGeneration) return;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      retryStateRef.current = { client: undefined, key: null, attempt: 0 };
    }

    identifyWithTimeout(
      targetClient,
      targetContext,
      IDENTIFY_ATTEMPT_TIMEOUT_MS,
      handleIdentifySuccess
    ).then(handleIdentifySuccess, err => {
      if (!mountedRef.current || generationRef.current !== myGeneration) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error(
        '[LaunchDarkly] identify failed, flags still evaluating the previous context:',
        err
      );
      const sameTarget =
        retryStateRef.current.client === targetClient && retryStateRef.current.key === targetKey;
      const attempt = sameTarget ? retryStateRef.current.attempt + 1 : 1;
      retryStateRef.current = { client: targetClient, key: targetKey, attempt };
      if (attempt > MAX_IDENTIFY_RETRIES) {
        // eslint-disable-next-line no-console
        console.error(
          `[LaunchDarkly] giving up after ${attempt} failed attempts for this identity; ` +
            'no further attempts for it until the identity or client changes.'
        );
        onGaveUp(targetKey);
        return;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current || generationRef.current !== myGeneration) return;
        requestedRef.current = { client: undefined, key: null };
        identifyLatestRef.current();
      }, IDENTIFY_RETRY_DELAY_MS);
    });
  };

  useEffect(() => {
    identifyLatestRef.current();
  }, [client, contextKey]);

  useEffect(() => {
    if (!client || !ldClientError) return;
    const onLateReady = () => {
      if (!mountedRef.current) return;
      onSettled(null);
      requestedRef.current = { client: undefined, key: null };
      identifyLatestRef.current();
    };
    client.on('ready', onLateReady);
    return () => {
      client.off('ready', onLateReady);
    };
  }, [client, ldClientError, onSettled]);

  return null;
}

/**
 * Surfaces an SDK init failure (bad client-side ID, network blocked, etc.)
 * as a console error instead of the silent "every flag just reads its
 * default" behavior LDProvider falls back to. That fallback is the right
 * runtime behavior (fail closed, don't break the app) — it just shouldn't
 * also be invisible to whoever is debugging why a flag they flipped isn't
 * showing up.
 */
function LaunchDarklyErrorLogger() {
  const error = useLDClientError();
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[LaunchDarkly] client failed to initialize, flags are reading defaults:',
        error
      );
    }
  }, [error]);
  return null;
}

/**
 * Wraps the app in LaunchDarkly's context when a client-side ID is
 * configured (front/.env.local — see config/featureFlags.ts), so
 * `useFeatureFlag()` (hooks/useFeatureFlag.ts) works anywhere below it.
 * Renders children directly, with no LD context, when it isn't: every flag
 * then just reads its catalog default (fail closed) instead of the whole
 * app refusing to boot without a LaunchDarkly key.
 *
 * Mounts with the anonymous context immediately (so the client initializes
 * fast, without waiting on `/me`), then `LaunchDarklyIdentitySync`
 * re-identifies with the real context once available. `timeout={5}` bounds
 * `waitForInitialization()` — without it, a black-holed request (proxy,
 * captive portal) never sets `ldClient` and `FlagsReadyContext` stays false
 * forever instead of falling through to the catalog default (AB#29037).
 *
 * No `options.bootstrap: 'localStorage'` — a cached "on" value would survive
 * a LaunchDarkly outage or a blocked request indefinitely (turning "flag
 * off" from a reliable kill switch into a best-effort one).
 */
export default function LaunchDarklyContext({ children }: { children: React.ReactNode }) {
  const { meResponse, effectiveRole, orgId, isLoading: authLoading } = useAuth();
  const { siteId: activeSiteId, siteKey: activeSiteKey } = useActiveSite({ reconcile: false });
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const isRealManager = personaForRole(effectiveRole) === 'manager';
  const isFleet = isRealManager && (selectedSiteId == null || selectedSiteId === FLEET_ID);
  const siteContextKey = buildSiteContextKey(orgId, isFleet, activeSiteId);
  const siteReadableKey = isFleet ? '' : activeSiteKey;
  const context = useMemo(
    () =>
      buildContext(meResponse, effectiveRole, orgId, {
        key: siteContextKey,
        siteKey: siteReadableKey,
        fleet: isFleet,
      }),
    [meResponse, effectiveRole, orgId, siteContextKey, siteReadableKey, isFleet]
  );
  const contextKey = JSON.stringify(context);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [gaveUpKey, setGaveUpKey] = useState<string | null>(null);

  if (!LAUNCHDARKLY_CLIENT_SIDE_ID) {
    return <FlagsReadyContext.Provider value={true}>{children}</FlagsReadyContext.Provider>;
  }

  const ready = !authLoading && settledKey === contextKey;
  const unreachable = !authLoading && !ready && gaveUpKey === contextKey;

  return (
    <LDProvider
      clientSideID={LAUNCHDARKLY_CLIENT_SIDE_ID}
      context={context}
      timeout={5}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <LaunchDarklyIdentitySync
        context={context}
        contextKey={contextKey}
        onSettled={setSettledKey}
        onGaveUp={setGaveUpKey}
      />
      <LaunchDarklyErrorLogger />
      <FlagsReadyContext.Provider value={ready}>
        <FlagsUnreachableContext.Provider value={unreachable}>
          {children}
        </FlagsUnreachableContext.Provider>
      </FlagsReadyContext.Provider>
    </LDProvider>
  );
}
